'use client';

import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:embedded-pricing-table');
import { STRIPE_PRICING_TABLE_ID, STRIPE_PUBLISHABLE_KEY } from '../env';
import { useCheckChatAccessQuery, useCheckRecentPaymentQuery } from '../hooks/use-gateway-queries';
import { useStripeCheckoutMonitor } from '../hooks/use-stripe-checkout-monitor';
import { useAuth } from '../services/auth-service';
import { toast } from '../utils/toast';

type StripePricingTableProps = {
  pricingTableId: string;
  publishableKey: string;
  customerSessionClientSecret: string;
  clientReferenceId?: string;
  successUrl?: string;
  cancelUrl?: string;
};

// Declare the Stripe pricing table custom element for TypeScript
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & StripePricingTableProps,
        HTMLElement
      >;
    }
  }
}

export type EmbeddedPricingTableProps = {
  customerSessionClientSecret: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
};

/**
 * EmbeddedPricingTable - Displays Stripe pricing table directly in the app
 *
 * Uses Stripe's web component to embed the pricing table without opening
 * a separate window, providing a more seamless user experience.
 */
export const EmbeddedPricingTable: React.FC<EmbeddedPricingTableProps> = ({
  customerSessionClientSecret,
  onSuccess,
  onError,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = React.useState(true);
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const [checkPaymentStatus, setCheckPaymentStatus] = React.useState(false);
  const [isPollingBackground, setIsPollingBackground] = React.useState(false);

  // Query for checking chat access (entitlement)
  const { refetch: refetchChatAccess } = useCheckChatAccessQuery(
    isAuthenticated && !!user?.organizationId
  );

  // Query for checking recent payment status
  const { data: paymentData, isLoading: isCheckingPayment } =
    useCheckRecentPaymentQuery(checkPaymentStatus);

  // Load Stripe pricing table script
  React.useEffect(() => {
    // Check if script is already loaded
    if (document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]')) {
      setScriptLoaded(true);
      setIsLoading(false);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;

    script.onload = () => {
      logger.info('✅ [EMBEDDED-PRICING-TABLE] Stripe script loaded');
      setScriptLoaded(true);
      setIsLoading(false);
    };

    script.onerror = () => {
      logger.error('❌ [EMBEDDED-PRICING-TABLE] Failed to load Stripe script');
      setIsLoading(false);
      onError?.('Failed to load Stripe pricing table. Please check your internet connection.');
    };

    document.body.appendChild(script);

    return () => {
      // Don't remove script on unmount - it can be reused
    };
  }, [onError]);

  // Debug logging
  React.useEffect(() => {
    logger.info('🔍 [EMBEDDED-PRICING-TABLE] Render state:', {
      scriptLoaded,
      isLoading,
      isPollingBackground,
      hasTableId: !!STRIPE_PRICING_TABLE_ID,
      hasPubKey: !!STRIPE_PUBLISHABLE_KEY,
      hasClientSecret: !!customerSessionClientSecret,
      tableId: STRIPE_PRICING_TABLE_ID,
      pubKey: STRIPE_PUBLISHABLE_KEY,
    });
  }, [scriptLoaded, isLoading, isPollingBackground, customerSessionClientSecret]);

  // Background polling for entitlement after payment - non-blocking!
  React.useEffect(() => {
    if (!isPollingBackground) return;

    logger.info('🔄 [EMBEDDED-PRICING-TABLE] Starting background entitlement polling...');

    let pollCount = 0;
    const maxPolls = 45; // 45 attempts = 90 seconds
    const intervalMs = 2000; // Check every 2 seconds

    const pollInterval = setInterval(async () => {
      pollCount++;
      logger.info(`🔄 [EMBEDDED-PRICING-TABLE] Background poll ${pollCount}/${maxPolls}`);

      try {
        const result = await refetchChatAccess();
        const hasAccess = result?.data?.body?.hasAccess;

        if (hasAccess) {
          logger.info('✅ [EMBEDDED-PRICING-TABLE] Entitlement granted via background poll!');
          clearInterval(pollInterval);
          setIsPollingBackground(false);

          // Show success toast
          toast.success(
            'Subscription Active! 🎉',
            'Your subscription is now active. You can start using chat!'
          );

          // Call onSuccess callback
          onSuccess?.();
          return;
        }
      } catch (error) {
        logger.error('❌ [EMBEDDED-PRICING-TABLE] Background poll error:', error);
      }

      // Stop after max attempts
      if (pollCount >= maxPolls) {
        logger.warn('⚠️ [EMBEDDED-PRICING-TABLE] Background polling timeout after 90 seconds');
        clearInterval(pollInterval);
        setIsPollingBackground(false);

        toast.info(
          'Subscription Activating',
          'Your payment was successful. Activation is taking longer than usual - please check the billing panel or refresh in a few minutes.'
        );
      }
    }, intervalMs);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isPollingBackground, refetchChatAccess, onSuccess]);

  // Handle payment status check result
  React.useEffect(() => {
    if (!checkPaymentStatus || isCheckingPayment) return;

    if (paymentData) {
      logger.info('💳 [EMBEDDED-PRICING-TABLE] Payment status check result:', paymentData);

      if (paymentData.body.paymentCompleted) {
        logger.info(
          '✅ [EMBEDDED-PRICING-TABLE] Payment confirmed via Stripe API! Starting background polling'
        );

        // Show success toast immediately
        toast.success(
          'Payment Successful! 💳',
          "Your subscription is being activated. You'll be notified when it's ready (usually < 1 minute)."
        );

        // Start background polling (non-blocking)
        setIsPollingBackground(true);
      } else {
        logger.info(
          'ℹ️ [EMBEDDED-PRICING-TABLE] No payment detected - checkout was likely cancelled'
        );
        // Don't show error - user might have just closed window to review
      }

      // Reset the check flag
      setCheckPaymentStatus(false);
    }
  }, [paymentData, isCheckingPayment, checkPaymentStatus]);

  // CHROME EXTENSION REQUIRED - No fallback!
  //
  // How it works:
  // 1. User clicks "Subscribe" in embedded pricing table
  // 2. Stripe iframe tries to navigate parent window to checkout
  // 3. Extension intercepts at webNavigation API level (before navigation)
  // 4. Extension opens Stripe in NEW tab (web app stays alive!)
  // 5. Extension injects content script into Stripe page
  // 6. Content script watches DOM with MutationObserver
  // 7. User completes payment
  // 8. Stripe page updates DOM to show "Thanks for subscribing"
  // 9. Content script detects success text
  // 10. Extension notifies web app
  // 11. Extension auto-closes Stripe tab
  // 12. Web app triggers background polling for entitlement
  // 13. Chat access granted
  //
  // This replicates desktop Electron's BrowserWindow monitoring approach!

  const stripeMonitor = useStripeCheckoutMonitor({
    onSuccess: sessionId => {
      logger.info('✅ [EMBEDDED-PRICING-TABLE] Payment success detected from extension!', {
        sessionId,
      });

      // Show success toast immediately
      toast.success(
        'Payment Successful! 💳',
        "Your subscription is being activated. You'll be notified when it's ready (usually < 1 minute)."
      );

      // Start background polling immediately (non-blocking)
      setIsPollingBackground(true);

      // Also do an immediate refetch (optimistic)
      refetchChatAccess().catch(error => {
        logger.error('❌ [EMBEDDED-PRICING-TABLE] Immediate refetch failed:', error);
      });
    },
    debug: true,
  });

  // NOTE: The Stripe pricing table uses an iframe that navigates the parent window.
  // We can't intercept this from JavaScript due to cross-origin security.
  //
  // EXTENSION MODE: Extension intercepts at webNavigation level (before navigation happens)
  // - Opens Stripe in new tab
  // - Keeps web app alive
  // - Monitors new tab for success
  // - Desktop-like experience!
  //
  // REDIRECT MODE: Let navigation happen, detect success on redirect back
  // - Requires Stripe Dashboard config
  //
  // The hybrid monitor handles both modes automatically.

  // Listen for Stripe checkout events from Electron
  React.useEffect(() => {
    // Check if we're in Electron
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).electronAPI) {
      const handleCheckoutEvent = () => {
        logger.info(
          '🔍 [EMBEDDED-PRICING-TABLE] Checkout window closed, checking payment status via Stripe API'
        );
        setCheckPaymentStatus(true); // Trigger payment status check
      };

      // Listen for the IPC events
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const electronAPI = (window as any).electronAPI;
      const cleanupFuncs: Array<() => void> = [];

      if (electronAPI.onStripeCheckoutSuccess) {
        logger.info('🔗 [EMBEDDED-PRICING-TABLE] Registering stripe-checkout-success listener');
        const cleanup = electronAPI.onStripeCheckoutSuccess(handleCheckoutEvent);
        if (cleanup) cleanupFuncs.push(cleanup);
      }

      // Also listen for modal close event
      if (electronAPI.onStripeCheckoutClosed) {
        logger.info('🔗 [EMBEDDED-PRICING-TABLE] Registering stripe-checkout-closed listener');
        const cleanup = electronAPI.onStripeCheckoutClosed(handleCheckoutEvent);
        if (cleanup) cleanupFuncs.push(cleanup);
      }

      return () => {
        cleanupFuncs.forEach(cleanup => cleanup());
      };
    }

    return undefined;
  }, []);

  if (!STRIPE_PRICING_TABLE_ID || !STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="text-center p-8">
        <p className="text-destructive">Stripe configuration is missing. Please contact support.</p>
      </div>
    );
  }

  // No blocking processing state - background polling only!

  if (isLoading) {
    return (
      <div className="text-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading pricing options...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full px-4 py-6 overflow-y-auto">
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-bold mb-2">Choose Your Plan</h2>
        <p className="text-muted-foreground">Unlock the full potential of CtrlEnter</p>
      </div>

      {scriptLoaded ? (
        <div className="relative rounded-lg overflow-visible shadow-xl border border-border bg-card p-8 mb-6">
          {/* Styled container for Stripe pricing table */}
          <style>
            {`
 /* Custom styling for Stripe pricing table */
 stripe-pricing-table {
 width: 100%;
 min-height: 600px;
 display: block;
 position: relative;
 z-index: 1;
 }

 /* Try to blend with dark theme */
 stripe-pricing-table::part(container) {
 background: transparent !important;
 }

 /* Ensure Stripe checkout stays within bounds */
 stripe-pricing-table iframe {
 position: relative !important;
 }
 `}
          </style>
          {/* @ts-expect-error - Stripe custom element */}
          <stripe-pricing-table
            pricing-table-id={STRIPE_PRICING_TABLE_ID}
            publishable-key={STRIPE_PUBLISHABLE_KEY}
            customer-session-client-secret={customerSessionClientSecret}
            client-reference-id={user?.id}
          />
          {/* 
 CHROME EXTENSION PAYMENT DETECTION (Extension Required!)
 
 The web app requires the Chrome extension for payment detection.
 
 Flow:
 1. Extension intercepts Stripe checkout navigation (webNavigation API)
 2. Opens Stripe in new tab (web app stays alive)
 3. Content script monitors Stripe page DOM for success text
 4. Detects "Thanks for subscribing" message
 5. Notifies web app via postMessage
 6. Triggers background polling for entitlement
 7. Auto-closes Stripe tab
 
 This provides desktop Electron-like experience where checkout happens
 in a separate context and the main app never loses state.
 
 See:
 - /clients/extension/src/background/service-worker.ts (webNavigation interception)
 - /clients/extension/src/content/stripe-monitor.ts (DOM monitoring)
 - /clients/web/src/hooks/use-stripe-checkout-monitor.ts (web app integration)
 */}
        </div>
      ) : (
        <div className="text-center p-8">
          <p className="text-destructive">Failed to load pricing table</p>
        </div>
      )}
    </div>
  );
};

EmbeddedPricingTable.displayName = 'EmbeddedPricingTable';
